import { DatePickerAlt, type DatePickerAltProps } from '@SBGSports/catapult-core';
import { useGetSetting, KEY_DATE_LOCALE, KEY_DEFAULT_LANGUAGE_WEB } from 'api';
import { getLocale, useDateFormat } from 'shared/utils/date-time-utils';
import { usePickerLabels } from './use-picker-labels';
import { assertValidDateProps } from './validate-date-props';

/**
 * Props for {@link DatePicker}. Accepts every prop on the design system's
 * `DatePickerAltProps` except `locale`, `format`, and `labels`, which the
 * wrapper injects from app settings and translations.
 *
 * Cmd+Click `DatePickerAltProps` in the import above to see the full prop list.
 */
export type DatePickerProps = Omit<DatePickerAltProps, 'locale' | 'format' | 'labels'>;

/**
 * Openfield single date picker. Thin wrapper around the design system's
 * `DatePickerAlt` that injects app context and applies sensible defaults.
 *
 * **Auto-injected (not overridable):**
 * - `locale`: from `KEY_DATE_LOCALE` + `KEY_DEFAULT_LANGUAGE_WEB` settings
 * - `format`: from `useDateFormat()` (dd/MM/yyyy or MM/dd/yyyy)
 * - `labels`: translated via `date_picker.*` i18n keys
 *
 * **Defaults (overridable via props):**
 * - `weekStart={1}` (Monday)
 * - `showClearButton`
 * - `showTodayButton`
 * - `commitMode="auto"`
 *
 * @example
 * ```tsx
 * const [date, setDate] = useState<Date | undefined>();
 * <DatePicker value={date} onChange={setDate} label="Start date" />
 * ```
 */
export const DatePicker = (props: DatePickerProps) => {
    // Fail fast on bad Date props before any hooks run (keeps hook order stable).
    assertValidDateProps('DatePicker', { value: props.value, minDate: props.minDate, maxDate: props.maxDate });

    const language = useGetSetting(KEY_DEFAULT_LANGUAGE_WEB)?.value ?? '';
    const dateLocale = useGetSetting(KEY_DATE_LOCALE)?.value ?? '';
    const locale = getLocale(language, dateLocale);
    const format = useDateFormat();
    const labels = usePickerLabels();

    return (
        <DatePickerAlt
            weekStart={1}
            showClearButton
            showTodayButton
            commitMode="auto"
            // Uppercased format as the default placeholder (DD/MM/YYYY). Overridable via props.
            placeholder={format.toUpperCase()}
            {...props}
            locale={locale}
            format={format}
            labels={labels}
        />
    );
};
